/**
 * The attribute, the cross-fade and the copy in `localStorage`.
 *
 * The rule about *which* theme somebody gets is not here — it is in
 * `src/lib/theme.ts`, which the server imports too. What is here is what the
 * browser then does with the answer.
 *
 * **No DOM either.** Everything below takes the element and the storage it
 * works on as arguments, typed structurally, for the same reason
 * `src/lib/pinch-zoom.ts` and `src/lib/receipt-file.ts` do: what is worth
 * testing about a theme switch is that one attribute changes and one class
 * lasts exactly 180 ms, and standing up a whole DOM implementation to watch
 * that happen is the slower way to find out less. `src/theme-document.ts` is
 * the four lines that reach for the real `document` and the real
 * `localStorage`.
 *
 * ## Why there is a copy in localStorage at all
 *
 * The choice lives on the user row; that is the record. But the row is behind
 * a request, and a request finishes long after the first paint — so a page
 * that only knew the row would render Midnight and then snap to Sandstone a
 * few hundred milliseconds later, on every single load. The copy in
 * `localStorage` is what the inline script in `index.html` reads *before the
 * first paint*, and it is the only reason there is no flash.
 *
 * It holds the **effective** theme, never the chosen one. A lapsed Premium
 * account has `sunset` on its row and `midnight` in storage, because storage
 * exists to answer "what did this screen look like last time", and last time
 * it looked like Midnight. Writing the chosen id there would reintroduce the
 * flash it exists to remove, one theme worse: a flash of a theme they are not
 * entitled to.
 */

import {
  THEME_ATTRIBUTE,
  THEME_FADE_MS,
  THEME_STORAGE_KEY,
  isThemeId,
} from './lib/theme'

/**
 * Set on `<html>` for the length of a switch, and nothing else: the transition
 * it turns on is scoped to it, so the first paint has no transition to run and
 * an ordinary hover is not dragged out to 180 ms. `src/index.css` has the
 * other end of this.
 */
export const SWITCHING_CLASS = 'theme-switching'

/** The two methods of `localStorage` this uses, and null for "there isn't one". */
export type ThemeStorage = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
} | null

/** The three things this does to `<html>`, and nothing else. */
export type ThemeRoot = {
  getAttribute(name: string): string | null
  setAttribute(name: string, value: string): void
  classList: { add(token: string): void; remove(token: string): void }
}

/**
 * What the last load ended up on, if anything, and if it is still plausible.
 *
 * In the browser it is the inline script in `index.html` that actually makes
 * this read — it has to happen before any module exists. This is the same
 * read as a function, for the same reason `THEME_ID_PATTERN` is exported: it
 * keeps the key spelled once in TypeScript, and it makes the round trip
 * through storage something `tests/theme-client.test.ts` can hold, rather than
 * a write nothing checks and a read nothing can reach.
 */
export function readStoredTheme(storage: ThemeStorage): string | null {
  try {
    const stored = storage?.getItem(THEME_STORAGE_KEY) ?? null
    return isThemeId(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * Remembers the *effective* theme for the next first paint. Storage being
 * full, disabled or blocked costs the next load its flash and nothing else,
 * so it is swallowed rather than surfaced.
 */
export function rememberTheme(id: string, storage: ThemeStorage): void {
  if (!isThemeId(id)) return
  try {
    storage?.setItem(THEME_STORAGE_KEY, id)
  } catch {
    /* not worth telling anybody about */
  }
}

/** What is on the element now — the id the current paint is using. */
export function currentTheme(root: ThemeRoot): string | null {
  const value = root.getAttribute(THEME_ATTRIBUTE)
  return isThemeId(value) ? value : null
}

let fadeTimer: ReturnType<typeof setTimeout> | undefined

/**
 * Puts `id` on the root, cross-fading unless there is nothing to fade from.
 *
 * The fade is a transition on the colour properties, turned on by a class for
 * exactly its own duration — see `SWITCHING_CLASS`. The first application of a
 * theme (from the inline script, or from a root that has no attribute yet) is
 * not a switch and is not faded: there is no previous colour on the screen,
 * and a 180 ms fade up from the browser's default white is a flash with extra
 * steps.
 *
 * Re-applying the theme that is already on is a no-op, so the session
 * arriving and confirming what `localStorage` already said costs nothing and
 * flashes nothing — which is the common case on every load.
 */
export function applyTheme(
  root: ThemeRoot,
  id: string,
  { fade = true }: { fade?: boolean } = {},
): void {
  if (!isThemeId(id)) return
  const from = currentTheme(root)
  if (from === id) return

  if (fade && from !== null) {
    root.classList.add(SWITCHING_CLASS)
    clearTimeout(fadeTimer)
    fadeTimer = setTimeout(() => root.classList.remove(SWITCHING_CLASS), THEME_FADE_MS)
  }

  root.setAttribute(THEME_ATTRIBUTE, id)
}
